"use client";

import {
    createUserWithEmailAndPassword,
    GoogleAuthProvider,
    OAuthProvider,
    sendEmailVerification,
    sendPasswordResetEmail,
    signInWithEmailAndPassword,
    signInWithPopup,
    updatePassword,
    updateProfile,
} from "firebase/auth";
import {auth} from "@/config/firebase";
import axios from "axios";

// Helper function to check auth availability
const checkAuthAvailable = () => {
    if (!auth) {
        throw new Error('Firebase authentication not configured');
    }
    return auth;
};

export const doCreateUserWithEmailAndPassword = async (
    email: string,
    password: string
) => {
    const authInstance = checkAuthAvailable();
    try {
        return await createUserWithEmailAndPassword(authInstance, email, password);
    } catch (error) {
        throw error;
    }
};

export const doSignInWithEmailAndPassword = (
    email: string,
    password: string
) => {
    const authInstance = checkAuthAvailable();
    try {
        return signInWithEmailAndPassword(authInstance, email, password);
    } catch (error) {
        throw error;
    }
};

export const doSignInWithGoogle = async () => {
    const authInstance = checkAuthAvailable();
    const provider = new GoogleAuthProvider();
    const result = await signInWithPopup(authInstance, provider);
    return result.user;
};

export const doSignInWithApple = async () => {
    const authInstance = checkAuthAvailable();
    const provider = new OAuthProvider("apple.com");
    provider.addScope("email");
    provider.addScope("name");
    const result = await signInWithPopup(authInstance, provider);
    return result.user;
};

export const doSignOut = () => {
    const authInstance = checkAuthAvailable();
    return authInstance.signOut();
};

export const doPasswordReset = (email: string) => {
    const authInstance = checkAuthAvailable();
    return sendPasswordResetEmail(authInstance, email);
};

export const doPasswordChange = (password: string) => {
    const authInstance = checkAuthAvailable();
    if (authInstance.currentUser) {
        return updatePassword(authInstance.currentUser, password);
    }
    throw new Error("No authenticated user");
};

export const doInsertDisplayName = (nickname: string) => {
    const authInstance = checkAuthAvailable();
    if (authInstance.currentUser) {
        return updateProfile(authInstance.currentUser, {
            displayName: nickname,
        });
    }
    throw new Error("No authenticated user");
};

export const doSendEmailVerification = () => {
    const authInstance = checkAuthAvailable();
    if (authInstance.currentUser) {
        return sendEmailVerification(authInstance.currentUser);
    }
    throw new Error("No authenticated user");
};

export const checkNickname = async (nickname: string) => {
    const checkNickname = await axios.post("/api/checkNickname", {
        nickname: nickname,
    });
    return checkNickname.data.data.users.length > 0;
};

export const refreshUserToken = async () => {
    const authInstance = checkAuthAvailable();
    if (authInstance.currentUser) {
        await authInstance.currentUser.getIdToken(true);
        return authInstance.currentUser;
    }
    throw new Error("No authenticated user");
};

// export const userReady = async () => {
//     return new Promise((resolve) => {
//         const interval = setInterval(async () => {
//             const response = await axios.post("/api/userReady", {
//                 firebaseId: auth.currentUser?.uid,
//             });
//             if (response.data.data.users[0].auth_complete) {
//                 clearInterval(interval);
//                 resolve(true);
//             }
//         }, 1000);
//     });
// };

export const emailConfirmed = async () => {
    const authInstance = checkAuthAvailable();
    return new Promise((resolve) => {
        const interval = setInterval(async () => {
            await authInstance.currentUser?.reload();
            if (authInstance.currentUser?.emailVerified) {
                clearInterval(interval);
                resolve(true);
            }
        }, 1000);
    });
};

export const insertUserHasura = async (email: any, nickname?: string, first_name?: string | null, last_name?: string | null, userPreferences?: any, avatar?: string | null) => {
    const authInstance = checkAuthAvailable();
    const data: any = {
        email: email,
        firebaseId: authInstance.currentUser?.uid,
        step: 2,
        first_name,
        last_name,
        avatar
    };

    // Aggiungi le preferenze utente se fornite
    if (userPreferences) {
        data.user_preferences = {
            data: userPreferences
        };
    }

    await axios.post("/api/insertUser", data);
    if (nickname) {
        await insertNickname(nickname);
    }
    return true;
};

export const checkUserEmail = async () => {
    const email = await emailConfirmed();
    if (email) {
        return true;
    }
};

export const insertNickname = async (nickname: string) => {
    const authInstance = checkAuthAvailable();
    const data: any = {
        nickname: nickname,
        firebaseId: authInstance.currentUser?.uid,
    };
    await axios.post("/api/insertNickname", data);
    await doInsertDisplayName(nickname);
    return true;
};
